import { AlertTriangle, Shield } from 'lucide-react';
import React, { useState, useEffect } from 'react';

import { Alert, AlertDescription } from '../ui/alert';

// SECURITY: Strict allowlist of permitted iframe sources
const ALLOWED_IFRAME_SOURCES = [
  '/api/swagger-ui/', // Only allow same-origin API documentation
];

// SECURITY: Restricted sandbox permissions
const SECURE_SANDBOX_PERMISSIONS = [
  'allow-same-origin', // Required for API documentation
  'allow-scripts', // Required for Swagger UI
];

const validateIframeSrc = url => {
  if (!url) return { isValid: false, reason: 'URL is required' };

  try {
    const parsedUrl = new URL(url, window.location.origin);

    // Block non-HTTPS URLs (except localhost for dev)
    if (parsedUrl.protocol !== 'https:' && parsedUrl.protocol !== 'http:') {
      return { isValid: false, reason: 'Only HTTPS URLs are allowed' };
    }

    // Check against allowlist
    const isAllowed = ALLOWED_IFRAME_SOURCES.some(allowedPath =>
      parsedUrl.pathname.startsWith(allowedPath)
    );

    if (!isAllowed) {
      return {
        isValid: false,
        reason: `URL path not in allowlist. Allowed: ${ALLOWED_IFRAME_SOURCES.join(', ')}`,
      };
    }

    return { isValid: true };
  } catch (error) {
    return { isValid: false, reason: 'Invalid URL format' };
  }
};

const SecureIframe = ({ src, title, className = '', style = {}, onLoad, onError }) => {
  const [validationResult, setValidationResult] = useState({ isValid: true });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const result = validateIframeSrc(src);
    setValidationResult(result);
  }, [src]);

  if (!validationResult.isValid) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          <strong>Security Warning:</strong> {validationResult.reason}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="relative w-full h-full">
      <div className="absolute top-2 right-2 z-10">
        <div className="flex items-center gap-1 bg-green-100 text-green-800 px-2 py-1 rounded text-xs">
          <Shield className="h-3 w-3" />
          <span>Secured</span>
        </div>
      </div>

      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ocean-600 mx-auto"></div>
            <p className="text-sm text-gray-600 mt-2">Loading secure content...</p>
          </div>
        </div>
      )}

      <iframe
        src={src}
        title={title}
        className={`w-full h-full border-0 ${className}`}
        style={style}
        onLoad={() => {
          setIsLoading(false);
          onLoad?.();
        }}
        onError={onError}
        sandbox={SECURE_SANDBOX_PERMISSIONS.join(' ')}
        loading="lazy"
        referrerPolicy="strict-origin-when-cross-origin"
        allow="none"
      />
    </div>
  );
};

export default SecureIframe;
